import { useCallback, useEffect, useMemo } from "react";
import { QueryObserver, useMutation, useMutationState, useQueryClient } from "@tanstack/react-query";
import { FormikErrors, useFormik } from "formik";
import { camelCase, isEqual } from "lodash";

import * as settingsServiceLib from "../../../../commons/libs/settings-service";
import { AssortmentType } from "../../../../commons/types/assortment";
import { InitialSetupStep, Settings, ToolSetting, ToolSettings } from "../../../../commons/types/settings";
import { useCommonAppDispatcher } from "../../store/hooks";
import { sha256Hex } from "../externals/hash";
import useOnMount from "../hooks/use-on-mount";
import usePrevious from "../hooks/use-previous";
import useQuery from "../hooks/use-query";

import lib from "./use-settings.lib";
import * as Types from "./use-settings.types";

const TOPIC = "settings" as const;
const queryKey = (keys: string[] = []) => [TOPIC, ...keys] as const;

/**
 * Hook for accessing application settings.
 *
 * *Note: Settings use staleTime: Infinity and will remain cached until explicitly invalidated.*
 *
 * ## Usage
 * There are two patterns for using this hook:
 *
 * ### During initialization of app
 * ```ts
 *  const settings = useSettings();
 *
 *  if (!settings.query.isSuccess) { // or `settings.query.isLoading` or `!settings.query.data`
 *    console.log("Loading settings...");
 *  } else {
 *    console.log("Customization", settings.query.data.customization);
 *  }
 *  ```
 * ### After initialization
 * Since we are using a `staleTime` of `Infinity`, we can safely presume that settings are loaded after initialization.
 * When we are deep in the app, we can use the getter properties to have easy access to the settings,
 * without having to worry about the loading state in every container.
 *
 * For example:
 * ```ts
 * const settings = useSettings();
 * console.log("Customization", settings.initialized.customization);
 * ```
 **/
export const useSettings = (options: Types.UseSettingsQueryOptions = {}) => {
  const queryClient = useQueryClient();
  const [dispatch, actions] = useCommonAppDispatcher();

  const query = useQuery({
    ...options,
    queryKey: queryKey(),
    queryFn: settingsServiceLib.getSettings,
    staleTime: Infinity, // Keep the cache data, until we mutate the settings via `useSetSettings`.
    onData: settings => {
      options.onData?.(settings);
    },
    onError: error => {
      dispatch(actions.error.set(error));
    }
  });

  const invalidateCache = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKey() });
  }, [queryClient]);

  return {
    /** The raw query return object of `useSettings`. */
    query,

    // Getters
    /** Use this to get defined settings after app initialization was done. */
    get initialized() {
      if (!query.data) {
        throw new Error(`
          'useSettings': 'query.data' is still undefined! Please only use this selector after initialization.
          Please make sure that this hook is initialized during the initialization of the app. (useInitCore & useInitApp)
          `);
      } else {
        return query.data;
      }
    },
    get isBodySizingEnabled() {
      return Boolean(this.initialized.toolSettings.smartfit.smartfitApiAuth);
    },
    get isBodySizingNotificationsEnabled() {
      return !this.isBodySizingEnabled && Boolean(this.initialized.toolSettings.smartfit.meta.toolVisible);
    },
    get isPrintingActive() {
      return Boolean(this.initialized.printing.active);
    },
    get hasMailToPrintSettings() {
      return Boolean(this.initialized.printing.mailToPrint.email);
    },
    get isMailActive() {
      return Boolean(this.initialized.mail.active);
    },
    get hasMailSettings() {
      return Boolean(this.initialized.mail.fromMail);
    },
    get assortmentPriceSettings() {
      return lib.assortmentPriceSettings(this.initialized);
    },
    get assortmentFilterSettings() {
      return lib.assortmentFilterSettings(this.initialized);
    },

    // Actions
    get getToolSettingsByKey() {
      return <TS extends ToolSetting>(toolKey: keyof ToolSettings): TS => {
        /** This is necessary because internal tool keys are dash-cased, while the properties of its settings are camelCased.
         * Compare the `ToolKey` type with the entries of `ToolSettings`. */
        const convertedToolKey = camelCase(toolKey) as keyof ToolSettings;
        return this.initialized.toolSettings[convertedToolKey] as TS;
      };
    },
    /** Invalidate the settings cache. */
    invalidateCache
  };
};

export const useSettingsObserver = ({
  onUpdatedSettings
}: {
  /**
   * *Unmemoized* callback that will be called when settings are updated (Previous settings != New settings).
   * The callback should be memoized in the parent component using useCallback.
   * */
  onUpdatedSettings: (settings: Settings) => void;
}) => {
  const queryClient = useQueryClient();

  const settings = useSettings();
  const prevSettings = usePrevious(settings.query.data);
  const observer = useMemo(() => new QueryObserver<Settings>(queryClient, { queryKey: queryKey() }), [queryClient]);

  useEffect(() => {
    const unsubscribe = observer.subscribe(result => {
      if (result.status === "success" && result.data && prevSettings && !isEqual(result.data, prevSettings)) {
        onUpdatedSettings(result.data);
      }
    });

    return unsubscribe;
  }, [observer, onUpdatedSettings, prevSettings]);
};

const useSetPersonalCode = (options: Types.UseSettingsMutationOptions<Settings["personalCode"]> = {}) => {
  return useMutation({
    ...options,
    mutationKey: queryKey(["personalCode"]),
    mutationFn: personalCode =>
      settingsServiceLib.storeSetting<Settings["personalCode"]>(["personalCode"], sha256Hex(personalCode ?? ""))
  });
};

const useSetCurrency = (options: Types.UseSettingsMutationOptions<Settings["currency"]> = {}) => {
  return useMutation({
    ...options,
    mutationKey: queryKey(["currency"]),
    mutationFn: currency => settingsServiceLib.storeSetting<Settings["currency"]>(["currency"], currency)
  });
};

const useSetCustomization = (options: Types.UseSettingsMutationOptions<Partial<Settings["customization"]>> = {}) => {
  const settings = useSettings();
  const currentCustomizationSettings = settings.query.data?.customization;

  return useMutation({
    ...options,
    mutationKey: queryKey(["customization"]),
    mutationFn: customizationSettings => {
      return settingsServiceLib.storeSetting<Partial<Settings["customization"]>>(["customization"], {
        ...currentCustomizationSettings,
        ...customizationSettings
      });
    }
  });
};

const useSetMail = (options: Types.UseSettingsMutationOptions<Partial<Settings["mail"]>> = {}) => {
  const settings = useSettings();
  const currentMailSettings = settings.query.data?.mail;

  return useMutation({
    ...options,
    mutationKey: queryKey(["mail"]),
    mutationFn: mailSettings => {
      return settingsServiceLib.storeSetting<Partial<Settings["mail"]>>(["mail"], {
        ...currentMailSettings,
        ...mailSettings
      });
    }
  });
};

const useSetPrinting = (options: Types.UseSettingsMutationOptions<Partial<Settings["printing"]>> = {}) => {
  const settings = useSettings();
  const currentPrintingSettings = settings.query.data?.printing;

  return useMutation({
    ...options,
    mutationKey: queryKey(["printing"]),
    mutationFn: printingSettings => {
      return settingsServiceLib.storeSetting<Partial<Settings["printing"]>>(["printing"], {
        ...currentPrintingSettings,
        ...printingSettings
      });
    }
  });
};

const useSetBookmarks = (options: Types.UseSettingsMutationOptions<Partial<Settings["bookmarks"]>> = {}) => {
  const settings = useSettings();
  const currentBookmarksSettings = settings.query.data?.bookmarks;

  return useMutation({
    ...options,
    mutationKey: queryKey(["bookmarks"]),
    mutationFn: bookmarksSettings => {
      return settingsServiceLib.storeSetting<Partial<Settings["bookmarks"]>>(["bookmarks"], {
        ...currentBookmarksSettings,
        ...bookmarksSettings
      });
    }
  });
};

const useSetAssortment = (options: Types.UseSettingsMutationOptions<Partial<Settings["assortment"]>> = {}) => {
  const settings = useSettings();
  const currentAssortmentSettings = settings.query.data?.assortment;

  return useMutation({
    ...options,
    mutationKey: queryKey(["assortment"]),
    mutationFn: assortmentSettings => {
      return settingsServiceLib.storeSetting<Partial<Settings["assortment"]>>(["assortment"], {
        ...currentAssortmentSettings,
        ...assortmentSettings
      });
    }
  });
};

const useSetAssortmentType = (options: Types.UseSettingsMutationOptions<Settings["assortment"]["type"]> = {}) => {
  const settings = useSettings();
  const currentAssortmentSettings = settings.query.data?.assortment;

  return useMutation({
    ...options,
    mutationKey: queryKey(["assortment", "type"]),
    mutationFn: assortmentType => {
      return settingsServiceLib.storeSetting<Partial<Settings["assortment"]>>(["assortment"], {
        ...currentAssortmentSettings,
        ...(assortmentType === AssortmentType.Automatic ? settings.initialized.assortment.priceSettings : {}),
        type: assortmentType
      });
    }
  });
};

const useSetToolSettings = <TS extends ToolSetting>(
  toolKey: keyof Settings["toolSettings"],
  options: Types.UseSettingsMutationOptions<TS> & {
    mutationKey?: string[];
  } = {}
) => {
  return useMutation({
    ...options,
    mutationKey: queryKey(["toolSettings", toolKey, ...(options.mutationKey ?? [])]),
    mutationFn: toolSettings => {
      return settingsServiceLib.storeSetting<Settings["toolSettings"][typeof toolKey]>(
        ["toolSettings", toolKey],
        toolSettings
      );
    }
  });
};

/**
 * For a given tool key, the meta properties `touchedSettings` and `viewedSettings` are set to `true`,
 * while preserving the reset of the `meta` properties.
 */
const useResetToolSettings = <TS extends ToolSetting>(toolKey: keyof Settings["toolSettings"]) => {
  const settings = useSettings();
  const toolSettings = settings.getToolSettingsByKey<TS>(toolKey);
  const setToolSettings = useSetToolSettings<TS>(toolKey, {
    mutationKey: ["reset"]
  });

  const options = {
    meta: {
      ...toolSettings.meta,
      touchedSettings: true,
      viewedSettings: true
    }
  } as TS;

  return {
    mutate: () => setToolSettings.mutate(options),
    mutateAsync: () => setToolSettings.mutateAsync(options),
    mutation: setToolSettings
  };
};

const useSetToolSettingsViewed = <TS extends ToolSetting>(toolKey: keyof Settings["toolSettings"]) => {
  const settings = useSettings();
  const toolSettings = settings.getToolSettingsByKey<TS>(toolKey);
  const setToolSettings = useSetToolSettings<TS>(toolKey, { mutationKey: ["viewed"] });

  const options: TS = {
    ...toolSettings,
    meta: {
      ...toolSettings.meta,
      viewedSettings: true
    }
  };

  return {
    mutate: () => setToolSettings.mutate(options),
    mutateAsync: () => setToolSettings.mutateAsync(options),
    mutation: setToolSettings
  };
};

const useSetLastSuccessfulUpdate = (options: Types.UseSettingsMutationOptions<void> = {}) => {
  return useMutation({
    ...options,
    mutationKey: queryKey(["updater", "lastSuccessfulUpdate"]),
    mutationFn: () =>
      settingsServiceLib.storeSetting<Settings["updater"]["lastSuccessfulUpdate"]>(
        ["updater", "lastSuccessfulUpdate"],
        new Date().getTime()
      )
  });
};

const useSetFinishedInitialSetupStep = ({
  settingsQueryOptions = {},
  mutationOptions = {}
}: {
  settingsQueryOptions?: Types.UseSettingsQueryOptions;
  mutationOptions?: Types.UseSettingsMutationOptions<Settings["initialSetup"]["finishedSteps"][number]>;
}) => {
  const settings = useSettings(settingsQueryOptions);

  return useMutation({
    ...mutationOptions,
    mutationKey: queryKey(["initialSetup", "finishedSteps"]),
    mutationFn: step => {
      const prevInitialSetup = settings.initialized.initialSetup;

      return settingsServiceLib.storeSetting<Settings["initialSetup"]>(["initialSetup"], {
        firstFinished:
          step === InitialSetupStep.Done && !prevInitialSetup.firstFinished
            ? new Date().toISOString()
            : prevInitialSetup.firstFinished,
        finishedSteps:
          prevInitialSetup.finishedSteps.indexOf(step) === -1
            ? [...prevInitialSetup.finishedSteps, step]
            : prevInitialSetup.finishedSteps
      });
    }
  });
};

const useDismissVeloconnectEndpointModal = (options: Types.UseSettingsMutationOptions<string> = {}) => {
  return useMutation({
    ...options,
    mutationKey: queryKey(["veloconnect", "modalDismissed"]),
    mutationFn: endpointKey => {
      return settingsServiceLib.storeSetting<Settings["veloconnect"][typeof endpointKey]["modalDismissed"]>(
        ["veloconnect", endpointKey, "modalDismissed"],
        true
      );
    }
  });
};

/**
 * Collection of all mutation hooks to set settings.
 *
 * **Note: You don't need to handle the settings invalidation yourself.**
 * This is handled by the queryClient mutation configuration in `src/client/kiosk|mobile/renderer-app.tsx`.
 *
 * @example
 * // Import the specific hook you need
 * import { useSetSettings } from './use-settings';
 * //..
 * const setCustomization = useSetSettings.customization.set();
 */
export const useSetSettings = {
  updater: {
    setLastSuccessfulUpdate: useSetLastSuccessfulUpdate
  },
  initialSetup: {
    setFinishedInitialSetupStep: useSetFinishedInitialSetupStep
  },
  personalCode: {
    set: useSetPersonalCode
  },
  currency: {
    set: useSetCurrency
  },
  customization: {
    set: useSetCustomization
  },
  bookmarks: {
    set: useSetBookmarks
  },
  mail: {
    set: useSetMail
  },
  printing: {
    set: useSetPrinting
  },
  veloconnect: {
    dismissModal: useDismissVeloconnectEndpointModal
  },
  assortment: {
    set: useSetAssortment,
    setType: useSetAssortmentType
  },
  toolSettings: {
    set: useSetToolSettings,
    reset: useResetToolSettings,
    setViewed: useSetToolSettingsViewed
  }
} as const satisfies { [K in keyof Settings]?: unknown };

/**
 * For a given path in the settings object, resets the value to its default value.
 *
 * @example
 * const resetSettings = useResetSettings();
 *
 * resetSettings.mutateAsync(["updater", "lastSuccessfulUpdate"])
 */
export const useResetSettings = (options: Types.UseSettingsMutationOptions<string[]> = {}) => {
  return useMutation({
    ...options,
    mutationKey: queryKey(["delete"]),
    mutationFn: settingsServiceLib.deleteSetting
  });
};

/**
 * Convenience hook for accessing and setting tool settings.
 * Does currently is not fit for pages, where the form values differ from the tool settings.
 * See examples usages by looking for its references in the code.
 */
export const useToolSettings = <TS extends ToolSetting = ToolSetting>(
  toolKey: keyof ToolSettings,
  options: Types.UseToolSettingsOptions<TS> = {}
) => {
  const settings = useSettings(options.query?.settings);
  const setToolSettingsViewed = useSetToolSettingsViewed(toolKey);

  const fallbackToolSettings = { meta: {} };
  const currentToolSettings = settings.getToolSettingsByKey<TS>(toolKey);
  const toolSettings = currentToolSettings ?? fallbackToolSettings;
  const setToolSettings = useSetToolSettings<TS>(toolKey);

  const formik = useFormik<TS>({
    enableReinitialize: true,
    initialValues: options.formik?.initialValues?.(toolSettings) ?? toolSettings,
    validate: (values: TS) => {
      if (options.formik?.validate) {
        const errors: FormikErrors<TS> = {};
        return options.formik.validate({ values, errors });
      } else {
        return {};
      }
    },
    onSubmit: async (values, helpers) => {
      helpers.setSubmitting(true);

      if (options.formik?.onSubmit) {
        await options.formik.onSubmit({ values, helpers, setToolSettings: setToolSettings.mutate });
      } else {
        setToolSettings.mutate({
          ...values,
          meta: {
            ...values.meta,
            touchedSettings: true
          }
        });
      }

      helpers.setSubmitting(false);
    }
  });

  useOnMount(() => {
    setToolSettingsViewed.mutate();
  });

  return {
    /** The raw query return object of `useSettings`. */
    settingsQuery: settings,
    /** The raw mutation return object of `useSetToolSettings`. */
    setToolSettingsMutation: setToolSettings,
    /** The current tool settings for the provided tool key. */
    toolSettings,
    /** The mutation function for setting the tool settings. */
    setToolSettings,
    /** The formik object for the tool settings form. */
    formik,
    /** Convenience property for checking whether the settings query is currently fetching. */
    isFetching: settings.query.isFetching,
    /** Convenience property for checking whether the settings mutation is currently pending. */
    isPending: setToolSettings.isPending,
    /** Convenience property for checking whether the settings mutation was successful. */
    isSuccess: setToolSettings.isSuccess
  };
};

export const useSetSettingsObserver = ({ onError }: { onError?: (error: Error) => void } = {}) => {
  const mutations = useMutationState({
    filters: { mutationKey: queryKey() }
  });

  const latest = mutations[mutations.length - 1];

  if (mutations.length > 0) {
    if (latest.status === "error" && latest.error) {
      onError?.(latest.error);
    }
  }
};
